import { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import api from '@/services/api'
import { getRoleSlugs } from '@/lib/permissions'

const AuthContext = createContext(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}

const roleConfig = {
  operador: {
    name: 'Operador de Salón',
    allowedRoutes: ['/dashboard', '/salon', '/profile'],
    permissions: ['view_salon_issues', 'create_issue', 'view_own_profile'],
  },
  tecnico: {
    name: 'Técnico',
    allowedRoutes: ['/dashboard', '/tecnico', '/profile', '/incidents'],
    permissions: [
      'view_assigned_issues',
      'accept_issue',
      'reject_issue',
      'resolve_issue',
      'view_own_profile',
    ],
  },
  encargado_zona: {
    name: 'Encargado de Zona',
    allowedRoutes: ['/dashboard', '/zona', '/profile'],
    permissions: [
      'view_zone_issues',
      'supervise_salons',
      'add_observations',
      'view_own_profile',
    ],
  },
  gestor: {
    name: 'Gestor',
    allowedRoutes: [
      '/dashboard',
      '/users',
      '/messages',
      '/logistics',
      '/profile',
      '/sites',
      '/incident-types',
      '/technician-routes',
      '/incidents',
    ],
    permissions: [
      'view_all',
      'manage_users',
      'manage_issues',
      'view_reports',
      'assign_technicians',
    ],
  },
  encargado: {
    name: 'Encargado',
    allowedRoutes: [
      '/dashboard',
      '/users',
      '/messages',
      '/logistics',
      '/profile',
      '/sites',
      '/incident-types',
      '/technician-routes',
      '/incidents',
    ],
    permissions: [
      'view_all',
      'manage_users',
      'manage_issues',
      'view_reports',
      'assign_technicians',
    ],
  },
  administrativo: {
    name: 'Administrativo',
    allowedRoutes: [
      '/dashboard',
      '/users',
      '/messages',
      '/logistics',
      '/profile',
      '/technician-routes',
      '/incidents',
    ],
    permissions: [
      'view_all',
      'manage_users',
      'manage_issues',
      'view_reports',
      'assign_technicians',
    ],
  },
  admin: {
    name: 'Admin',
    allowedRoutes: [
      '/dashboard',
      '/users',
      '/messages',
      '/logistics',
      '/profile',
      '/sites',
      '/incident-types',
      '/technician-routes',
      '/incidents',
      '/technicians',
      '/logs',
      '/revenue',
      '/sim/statistics',
      '/admin/time-tracking',
      '/vacations',
    ],
    permissions: [
      'view_all',
      'manage_users',
      'manage_issues',
      'view_reports',
      'assign_technicians',
    ],
  },
}

export const mapApiUser = (apiUser) => {
  const roleSlugs = getRoleSlugs(apiUser)
  const primaryRole = roleSlugs[0] || 'gestor'
  return {
    id: apiUser.id,
    name: apiUser.name,
    email: apiUser.email,
    phone: apiUser.phone ?? '',
    image: apiUser.image_url ?? null,
    isAvailable: apiUser.is_available ?? null,
    role: primaryRole,
    roles: roleSlugs,
    // Flags por usuario (no roles). Ver lib/permissions.js
    puedeRecaudar: !!apiUser.puede_recaudar,
    puedeTrackear: !!apiUser.puede_trackear,
    puedeGestionarTecnicos: !!apiUser.puede_gestionar_tecnicos,
    mensajes: [],
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')

    if (token && userData) {
      try {
        const parsed = JSON.parse(userData)
        setUser(parsed)
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        // El usuario cacheado puede estar desfasado (rol, zonas, permisos).
        // Lo refrescamos en segundo plano; si el token ya no vale, lo ignoramos
        // y deja que el interceptor de api.js haga su trabajo.
        api
          .get('/auth/me')
          .then(({ data }) => {
            const fresh = mapApiUser(data?.user ?? data)
            localStorage.setItem('user', JSON.stringify(fresh))
            setUser(fresh)
          })
          .catch(() => {})
      } catch (e) {
        localStorage.removeItem('user')
        localStorage.removeItem('token')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password })
      const { token, user: apiUser } = data
      const mapped = mapApiUser(apiUser)

      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(mapped))
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      setUser(mapped)
      toast.success(`Bienvenido ${mapped.name}`)
      // El operador no tiene Dashboard: aterriza en Incidencias.
      const home = mapped.role === 'operador' ? '/incidents' : '/dashboard'
      return { success: true, redirectTo: home, role: mapped.role }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al iniciar sesión')
      return { success: false, error: error.response?.data?.message }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
    toast.success('Sesión cerrada')
  }

  const isAuthenticated = () => !!user && !!localStorage.getItem('token')
  const hasRole = (role) => user?.role === role
  const hasPermission = (permission) =>
    !!user && (roleConfig[user.role]?.permissions || []).includes(permission)
  const canAccessRoute = (route) =>
    !!user && (roleConfig[user.role]?.allowedRoutes || []).includes(route)
  const getCurrentRoleInfo = () => (user ? roleConfig[user.role] : null)

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        logout,
        isAuthenticated,
        hasRole,
        hasPermission,
        canAccessRoute,
        getCurrentRoleInfo,
        roleConfig,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
